import React from 'react';
import { SEO } from '@/components/SEO';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import Breadcrumbs from '@/components/ui/Breadcrumbs';
import LocationFAQ from '@/components/locations/LocationFAQ';
import LocationNeighborhoods from '@/components/locations/LocationNeighborhoods';
import LocationBlogLinks from '@/components/locations/LocationBlogLinks';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Phone, MapPin, Star, Droplets, Sparkles, Wind, Filter, Brush, Sun } from 'lucide-react';

const ThousandOaksPage = () => {
  const navigate = useNavigate();
  
  const locationSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Ventura Power Washing - Thousand Oaks",
    "description": "Professional pressure washing, window cleaning, and carpet care services in Thousand Oaks, CA. Eco-friendly cleaning solutions for residential and commercial properties.",
    "url": "https://www.venturapowerwashing.com/thousand-oaks",
    "telephone": "(805) 464-7166",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Thousand Oaks",
      "addressRegion": "CA",
      "postalCode": "91360",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "34.1706",
      "longitude": "-118.8376"
    },
    "areaServed": {
      "@type": "City",
      "name": "Thousand Oaks",
      "containedInPlace": {
        "@type": "State",
        "name": "California"
      }
    },
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
      "opens": "07:00",
      "closes": "19:00"
    },
    "priceRange": "$$",
    "image": "https://www.venturapowerwashing.com/og-image.jpg",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Thousand Oaks Cleaning Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Pressure Washing Thousand Oaks",
            "description": "Professional pressure washing services for driveways, decks, and exterior surfaces in Thousand Oaks"
          }
        },
        {
          "@type": "Offer", 
          "itemOffered": {
            "@type": "Service",
            "name": "Window Cleaning Thousand Oaks",
            "description": "Streak-free window washing for residential and commercial properties in Thousand Oaks"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Carpet Cleaning Thousand Oaks",
            "description": "Deep carpet cleaning and stain removal services in Thousand Oaks"
          }
        }
      ]
    },
    "sameAs": [
      "https://www.facebook.com/venturapowerwashing",
      "https://www.instagram.com/venturacleaningco",
      "https://www.yelp.com/biz/ventura-power-washing-thousand-oaks"
    ]
  };

  const services = [
    {
      icon: <Droplets className="h-8 w-8 text-ventura-blue" />,
      title: "Pressure Washing",
      description: "Remove years of dirt, grime, and stains from your property's exterior surfaces.",
      link: "/services/pressure-washing"
    },
    {
      icon: <Sparkles className="h-8 w-8 text-ventura-blue" />,
      title: "Window Cleaning",
      description: "Crystal clear, streak-free windows that enhance curb appeal and natural light.",
      link: "/services/window-washing"
    },
    {
      icon: <Wind className="h-8 w-8 text-ventura-blue" />,
      title: "Carpet Cleaning",
      description: "Deep carpet cleaning that removes embedded dirt, stains, and allergens.",
      link: "/services/carpet-cleaning"
    },
    {
      icon: <Filter className="h-8 w-8 text-ventura-blue" />,
      title: "Gutter Cleaning",
      description: "Keep gutters flowing freely to protect your property from water damage.",
      link: "/services/gutter-cleaning"
    },
    {
      icon: <Brush className="h-8 w-8 text-ventura-blue" />,
      title: "Soft Wash",
      description: "Gentle cleaning for delicate surfaces like stucco, wood, and painted exteriors.",
      link: "/services/soft-wash"
    },
    {
      icon: <Sun className="h-8 w-8 text-ventura-blue" />,
      title: "Solar Panel Cleaning",
      description: "Maximize solar efficiency with professional panel cleaning and maintenance.",
      link: "/services/solar-panel-cleaning"
    }
  ];

  const faqs = [
    {
      question: "How much does pressure washing cost in Thousand Oaks, CA?",
      answer: "Most driveway, patio, and house pressure washing jobs in Thousand Oaks run $150-$400, depending on square footage and surface type. We provide a free, no-obligation estimate before any work begins."
    },
    {
      question: "How much does window cleaning cost in Thousand Oaks?",
      answer: "Residential window cleaning in Thousand Oaks typically costs $150-$300 for a standard home, inside and out."
    },
    {
      question: "Do you offer carpet cleaning in Thousand Oaks?",
      answer: "Yes. Whole-house carpet cleaning in Thousand Oaks generally runs $100-$300 depending on square footage and the number of rooms — a good fit for the larger homes common in Westlake Village and Lake Sherwood."
    },
    {
      question: "Is solar panel cleaning worth it for Thousand Oaks homes?",
      answer: "Yes — Thousand Oaks' dry inland climate and oak pollen build up on panels quickly and can reduce efficiency by 15-25%. Professional solar panel cleaning in Thousand Oaks typically costs $150-$350 and helps maximize energy production."
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title="Pressure Washing Thousand Oaks CA | Window & Carpet Cleaning Services"
        description="Professional pressure washing, window cleaning & carpet care in Thousand Oaks, CA. Serving 91360, 91361, 91362. Eco-friendly, licensed & insured. Free estimates!"
        keywords="pressure washing thousand oaks, window cleaning thousand oaks, carpet cleaning thousand oaks, power washing 91360, house washing thousand oaks"
        path="/thousand-oaks"
      >
        <script type="application/ld+json">
          {JSON.stringify(locationSchema)}
        </script>
      </SEO>

      <Navbar />
      <Breadcrumbs />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-ventura-blue to-ventura-darkBlue text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Professional Pressure Washing Services in Thousand Oaks, CA
            </h1>
            <p className="text-xl md:text-2xl mb-8 opacity-90">
              Expert window cleaning, pressure washing, and carpet care for Thousand Oaks residents and businesses in 91360, 91361, and 91362
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                className="bg-white text-ventura-blue hover:bg-gray-100 text-lg px-8 py-4"
                onClick={() => window.location.href = "tel:+18054647166"}
              >
                <Phone className="mr-2" size={20} />
                Call (805) 464-7166
              </Button>
              <Button 
                className="bg-ventura-green text-white hover:bg-green-600 text-lg px-8 py-4"
                onClick={() => navigate('/free-estimate')}
              >
                Get Free Estimate
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding bg-white">
        <div className="container mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">
            Our Thousand Oaks Cleaning Services
          </h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <Card key={index} className="card-shadow h-full hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6 text-center">
                  <div className="flex justify-center mb-4">
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-4">{service.title}</h3>
                  <p className="text-gray-600 mb-6">{service.description}</p>
                  <Link 
                    to={service.link}
                    className="text-ventura-blue hover:text-ventura-darkBlue font-semibold hover:underline"
                  >
                    Learn More →
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Local Area Information */}
      <section className="section-padding bg-ventura-lightGray">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-8">
              Why Do Thousand Oaks Homes Need a Local Cleaning Team?
            </h2>
            <p className="text-lg text-gray-700 text-center mb-8">
              From the oak-lined neighborhoods of Newbury Park to the upscale communities of Westlake Village, Thousand Oaks
              properties vary widely in exposure and landscaping. As your local Thousand Oaks cleaning professionals, we tailor
              reliable, eco-friendly cleaning to each neighborhood's specific needs, enhancing property value and curb appeal.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div>
                <h3 className="text-2xl font-bold mb-4">Thousand Oaks Areas We Serve</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-center"><MapPin className="h-4 w-4 mr-2 text-ventura-blue" />Newbury Park (91320)</li>
                  <li className="flex items-center"><MapPin className="h-4 w-4 mr-2 text-ventura-blue" />Westlake Village (91361)</li>
                  <li className="flex items-center"><MapPin className="h-4 w-4 mr-2 text-ventura-blue" />Thousand Oaks (91360, 91362)</li>
                  <li className="flex items-center"><MapPin className="h-4 w-4 mr-2 text-ventura-blue" />Conejo Valley</li>
                  <li className="flex items-center"><MapPin className="h-4 w-4 mr-2 text-ventura-blue" />Lake Sherwood</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-2xl font-bold mb-4">Why Choose Us in Thousand Oaks?</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-center"><Star className="h-4 w-4 mr-2 text-yellow-500" />Licensed & Insured</li>
                  <li className="flex items-center"><Star className="h-4 w-4 mr-2 text-yellow-500" />Eco-Friendly Products</li>
                  <li className="flex items-center"><Star className="h-4 w-4 mr-2 text-yellow-500" />Same-Day Estimates</li>
                  <li className="flex items-center"><Star className="h-4 w-4 mr-2 text-yellow-500" />Satisfaction Guarantee</li>
                  <li className="flex items-center"><Star className="h-4 w-4 mr-2 text-yellow-500" />Local Thousand Oaks Team</li>
                </ul>
              </div>
            </div>

            <div className="text-center">
              <p className="text-lg text-gray-700 mb-6">
                As your local Thousand Oaks cleaning professionals, we understand the unique needs of homes and businesses in Ventura County. From the oak-lined neighborhoods of Newbury Park to the upscale communities of Westlake Village, we provide reliable, eco-friendly cleaning services that enhance your property's value and curb appeal.
              </p>
              
              <Button 
                className="button-primary text-lg px-8 py-4"
                onClick={() => navigate('/free-estimate')}
              >
                Get Your Free Thousand Oaks Cleaning Quote Today
              </Button>
            </div>
          </div>
        </div>
      </section>

      <LocationNeighborhoods
        city="Thousand Oaks"
        neighborhoods={[
          { name: 'Oak Park', note: 'We serve Oak Park homes and HOA communities next to Thousand Oaks, including stucco soft washing, driveway and walkway cleaning, windows, gutters, and solar panels.' },
        ]}
      />
      <LocationFAQ city="Thousand Oaks" faqs={faqs} />
      <LocationBlogLinks city="Thousand Oaks" />

      <Footer />
    </div>
  );
};

export default ThousandOaksPage;